/**
 * Sound player — variant-pool wrapper around Howler with tier-based
 * mixing rules (per `docs/22_audio_layering_rules.md`).
 *
 * Layered protections:
 *
 *   - **Variant pool** — each event maps to N audio file variants. Random
 *     pick with anti-repeat per play. 34× tap_ding files = practically
 *     never the same sound twice in a row.
 *
 *   - **Per-event throttle** (`minGapMs`) — rejects plays too soon after
 *     the previous one.
 *
 *   - **Per-event polyphony cap** (`maxConcurrent`) — drops or smooth-stops
 *     extras of the same event.
 *
 *   - **Per-event fades** (`fadeInMs`/`fadeOutMs`) — never a hard start
 *     or hard stop, per JB 2026-05-25 «никакие звуки резко не обрывались».
 *
 *   - **Global polyphony cap** (`GLOBAL_POLYPHONY_CAP`) — across all
 *     non-bed events, max 6 concurrent. Beyond that the oldest one is
 *     smooth-stopped over 250ms.
 *
 *   - **Tier-based gating** — voice (tier 5) blocks feedback (tier 2);
 *     a peak in flight blocks another peak.
 *
 *   - **Tier-based ducking** — when a higher tier plays, lower tiers fade
 *     to a target multiplier for the duration. Voice ducks all to 0.2×,
 *     peak ducks 1-3 to 0.4×, event ducks breath to 0.5×.
 *
 *   - **Smooth stop** — every forced stop (cap exceeded, settings mute,
 *     loop replace) goes through `howl.fade → stop`, never `howl.stop`.
 *
 * Howler is in `html5: true` mode (workaround for the Chrome WebAudio
 * silent-after-autoplay-unlock quirk — see git log 2026-05-24).
 */
import { Howl } from 'howler';
import { SOUND_MANIFEST, type SoundDefinition, type SoundEventId } from './manifest.js';
import { useSoundSettings } from './settings.js';
import { emit } from '../telemetry/emit.js';

// ---------------------------------------------------------------------------
// Tunables
// ---------------------------------------------------------------------------

/**
 * Max simultaneous non-bed (tier ≥1) sounds. Above this, oldest is
 * smooth-stopped. Bed (ambient) loops don't count — they always play.
 */
const GLOBAL_POLYPHONY_CAP = 6;

/** Forced-stop fade length (when cap exceeded / loop replaced / etc.). */
const FORCED_STOP_FADE_MS = 250;

/** Ducking ramp + release ramp lengths. */
const DUCKING_RAMP_MS = 200;
const DUCKING_RELEASE_MS = 600;

/** Hang time after a ducker ends before lower tiers can release. */
const DUCKING_HANG_MS = 1000;

// ---------------------------------------------------------------------------
// Per-event pool (variants + last-played bookkeeping)
// ---------------------------------------------------------------------------

interface ActivePlay {
  readonly howl: Howl;
  readonly soundId: number;
  readonly eventId: SoundEventId;
  readonly tier: number;
  readonly baseVolume: number;
  readonly startedAt: number;
  /** Whether this play is currently ducked (volume = baseVolume × factor). */
  duckedTo: number | null;
  /** Timer id for scheduled fade-out (so we can cancel on early stop). */
  fadeOutTimer: number | null;
}

interface VariantPool {
  readonly howls: Howl[];
  lastPlayedIdx: number;
  lastPlayedAt: number;
  /** Currently playing instances of this event. */
  active: ActivePlay[];
  /** Loop bookkeeping — the howl currently looping (if any). */
  loopingHowl: Howl | null;
}

const pools = new Map<SoundEventId, VariantPool>();

/** Globally tracked active plays (across all events), excluding bed loops. */
const globalActive: ActivePlay[] = [];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getOrCreatePool(eventId: SoundEventId): VariantPool | null {
  const existing = pools.get(eventId);
  if (existing) return existing;
  const def = SOUND_MANIFEST[eventId];
  if (def.variants.length === 0) return null;
  const howls = def.variants.map((src) => {
    return new Howl({
      src: [src],
      loop: def.loop ?? false,
      volume: def.volume,
      preload: false,
      html5: true,
      onloaderror: (_id, err) => {
        emit('sound.failed', { event_id: eventId, reason: `load:${String(err)}` });
      },
      onplayerror: (_id, err) => {
        emit('sound.failed', { event_id: eventId, reason: `play:${String(err)}` });
      },
    });
  });
  const pool: VariantPool = {
    howls,
    lastPlayedIdx: -1,
    lastPlayedAt: 0,
    active: [],
    loopingHowl: null,
  };
  pools.set(eventId, pool);
  return pool;
}

function pickVariantIdx(pool: VariantPool): number {
  const n = pool.howls.length;
  if (n === 1) return 0;
  let idx = Math.floor(Math.random() * (n - 1));
  if (pool.lastPlayedIdx >= 0 && idx >= pool.lastPlayedIdx) idx++;
  return idx;
}

function releaseVoice(pool: VariantPool, soundId: number): void {
  const idx = pool.active.findIndex((a) => a.soundId === soundId);
  if (idx === -1) return;
  const entry = pool.active[idx];
  if (entry?.fadeOutTimer !== null && entry?.fadeOutTimer !== undefined) {
    window.clearTimeout(entry.fadeOutTimer);
  }
  pool.active.splice(idx, 1);
  const gIdx = globalActive.indexOf(entry!);
  if (gIdx !== -1) globalActive.splice(gIdx, 1);
  // After releasing a ducker, schedule release of ducked voices.
  scheduleDuckingRelease();
}

// ---------------------------------------------------------------------------
// Smooth stop (used for cap exceedance, loop replace, settings mute)
// ---------------------------------------------------------------------------

function smoothStop(active: ActivePlay): void {
  if (active.fadeOutTimer !== null) {
    window.clearTimeout(active.fadeOutTimer);
    active.fadeOutTimer = null;
  }
  const currentVol =
    (active.howl.volume(active.soundId) as number | undefined) ?? active.baseVolume;
  active.howl.fade(currentVol, 0, FORCED_STOP_FADE_MS, active.soundId);
  window.setTimeout(() => {
    try {
      active.howl.stop(active.soundId);
    } catch {
      /* Howler may have already cleaned up — safe to ignore. */
    }
  }, FORCED_STOP_FADE_MS + 30);
}

// ---------------------------------------------------------------------------
// Ducking
// ---------------------------------------------------------------------------

/**
 * Map (victim tier, trigger tier) → multiplier in [0,1], or null = no ducking.
 *
 * JB 2026-05-25: «эмбиент всегда должен звучать». Bed (tier 0) ducks
 * по более мягкому правилу — НИКОГДА ниже 0.5×, даже под voice. Так
 * ambient остаётся слышен даже когда Сай говорит — это «дыхание мира»
 * под её словами, не пустота.
 */
function duckingFactor(victimTier: number, triggerTier: number): number | null {
  if (triggerTier === 5) {
    if (victimTier === 0) return 0.5; // bed — мягко, но слышно
    if (victimTier < 5) return 0.2;
    return null;
  }
  if (triggerTier === 4) {
    if (victimTier === 0) return 0.7; // bed под peak — едва приглушённый
    if (victimTier >= 1 && victimTier <= 3) return 0.4;
    return null;
  }
  if (triggerTier === 3 && victimTier === 1) return 0.5;
  return null;
}

function applyDucking(triggerTier: number): void {
  for (const inst of globalActive) {
    const factor = duckingFactor(inst.tier, triggerTier);
    if (factor === null) continue;
    const target = inst.baseVolume * factor;
    const current = (inst.howl.volume(inst.soundId) as number | undefined) ?? inst.baseVolume;
    inst.howl.fade(current, target, DUCKING_RAMP_MS, inst.soundId);
    inst.duckedTo = factor;
  }
  // Bed loops live in pool.loopingHowl, not globalActive — duck them too.
  for (const [, pool] of pools) {
    if (pool.loopingHowl?.playing()) {
      const eventEntry = Array.from(pools.entries()).find(([, p]) => p === pool);
      if (!eventEntry) continue;
      const def = SOUND_MANIFEST[eventEntry[0]];
      const factor = duckingFactor(def.tier, triggerTier);
      if (factor === null) continue;
      // 2026-05-26 simplification: было (def.volume * factor * userVol) / def.volume —
      // def.volume сокращается. Если def.volume === 0 (теоретически
      // возможно для дефинишена с muted-by-default loop) — было division
      // by zero → NaN. Теперь: factor × effective.
      const target = factor * useSoundSettings.getState().effectiveVolume(def);
      const current = pool.loopingHowl.volume();
      pool.loopingHowl.fade(current, Math.max(0.001, target), DUCKING_RAMP_MS);
    }
  }
}

let releaseTimer: number | null = null;

/** Schedule a release of all ducked voices once no ducker remains. */
function scheduleDuckingRelease(): void {
  if (releaseTimer !== null) window.clearTimeout(releaseTimer);
  releaseTimer = window.setTimeout(() => {
    releaseTimer = null;
    // If any tier ≥3 voice is still active, hold the ducking.
    const stillDucking = globalActive.some((i) => i.tier >= 3);
    if (stillDucking) return;
    // Release globalActive members.
    for (const inst of globalActive) {
      if (inst.duckedTo === null) continue;
      const current = (inst.howl.volume(inst.soundId) as number | undefined) ?? 0;
      inst.howl.fade(current, inst.baseVolume, DUCKING_RELEASE_MS, inst.soundId);
      inst.duckedTo = null;
    }
    // Release ambient loops back to their base settings volume.
    for (const [eventId, pool] of pools) {
      if (pool.loopingHowl?.playing()) {
        const def = SOUND_MANIFEST[eventId];
        const target = useSoundSettings.getState().effectiveVolume(def);
        const current = pool.loopingHowl.volume();
        pool.loopingHowl.fade(current, target, DUCKING_RELEASE_MS);
      }
    }
  }, DUCKING_HANG_MS);
}

// ---------------------------------------------------------------------------
// Gating — should we even start this event?
// ---------------------------------------------------------------------------

function shouldGate(eventId: SoundEventId, def: SoundDefinition, pool: VariantPool): string | null {
  // Voice (tier 5) is active → block tier 2 feedback (taps go quiet during
  // Sai activation per doc 22 §3.3).
  if (def.tier === 2 && globalActive.some((i) => i.tier === 5)) {
    return 'gated_by_voice';
  }
  // Another peak (tier 4) is already in flight → block this peak.
  if (def.tier === 4 && globalActive.some((i) => i.tier === 4 && i.eventId !== eventId)) {
    return 'gated_by_active_peak';
  }
  // Per-event throttle.
  const minGap = def.minGapMs ?? 0;
  if (minGap > 0 && Date.now() - pool.lastPlayedAt < minGap) {
    return 'throttled';
  }
  // Per-event polyphony cap reached.
  const cap = def.maxConcurrent ?? Number.POSITIVE_INFINITY;
  if (pool.active.length >= cap) {
    return 'event_cap';
  }
  return null;
}

// ---------------------------------------------------------------------------
// Global polyphony enforcement
// ---------------------------------------------------------------------------

function enforceGlobalCap(): void {
  // globalActive doesn't include bed loops, so just compare length.
  while (globalActive.length >= GLOBAL_POLYPHONY_CAP) {
    // Smooth-stop the oldest. Don't kill peaks/voice prematurely if a
    // tap is the youngest — but in practice peaks have maxConcurrent=1
    // so this is rare.
    const oldest = globalActive.shift();
    if (oldest === undefined) break;
    // Also remove from its pool's active list.
    const pool = pools.get(oldest.eventId);
    if (pool !== undefined) {
      const idx = pool.active.indexOf(oldest);
      if (idx !== -1) pool.active.splice(idx, 1);
    }
    smoothStop(oldest);
  }
}

// ---------------------------------------------------------------------------
// Public: playSound
// ---------------------------------------------------------------------------

export function playSound(eventId: SoundEventId): void {
  const def = SOUND_MANIFEST[eventId];
  const settings = useSoundSettings.getState();
  const volume = settings.effectiveVolume(def);
  if (volume === 0) {
    emit('sound.skipped_muted', { event_id: eventId });
    return;
  }

  const pool = getOrCreatePool(eventId);
  if (pool === null) {
    emit('sound.failed', { event_id: eventId, reason: 'no_variants' });
    return;
  }

  // ---- Looping behavior (ambient drones — tier 0 bed) ----
  if (def.loop === true) {
    if (pool.loopingHowl?.playing() === true) {
      // Already playing — just adjust volume to current settings effective.
      pool.loopingHowl.volume(volume);
      return;
    }
    // Start the loop (or transition to a new variant). Stop old smoothly.
    if (pool.loopingHowl !== null) {
      const oldHowl = pool.loopingHowl;
      const oldVol = oldHowl.volume();
      oldHowl.fade(oldVol, 0, FORCED_STOP_FADE_MS);
      window.setTimeout(() => oldHowl.stop(), FORCED_STOP_FADE_MS + 30);
    }
    const idx = pickVariantIdx(pool);
    const howl = pool.howls[idx];
    if (howl === undefined) {
      emit('sound.failed', { event_id: eventId, reason: 'invalid_variant_idx' });
      return;
    }
    const fadeIn = def.fadeInMs ?? 0;
    howl.volume(fadeIn > 0 ? 0 : volume);
    howl.play();
    if (fadeIn > 0) howl.fade(0, volume, fadeIn);
    pool.loopingHowl = howl;
    pool.lastPlayedIdx = idx;
    pool.lastPlayedAt = Date.now();
    emit('sound.played', { event_id: eventId, variant_idx: idx });
    return;
  }

  // ---- Gating ----
  const gateReason = shouldGate(eventId, def, pool);
  if (gateReason !== null) {
    return; // silent drop — not a failure
  }

  // ---- Apply ducking to currently-active voices BEFORE starting the new one ----
  if (def.tier >= 3) {
    applyDucking(def.tier);
  }

  // ---- Global polyphony cap (enforce before adding the new voice) ----
  enforceGlobalCap();

  // ---- Choose variant + fire ----
  const idx = pickVariantIdx(pool);
  const howl = pool.howls[idx];
  if (howl === undefined) {
    emit('sound.failed', { event_id: eventId, reason: 'invalid_variant_idx' });
    return;
  }

  const fadeIn = def.fadeInMs ?? 0;
  const fadeOut = def.fadeOutMs ?? 0;

  if (fadeIn > 0) {
    howl.volume(0);
  } else {
    howl.volume(volume);
  }
  const soundId = howl.play();
  if (fadeIn > 0) {
    howl.fade(0, volume, fadeIn, soundId);
  }

  pool.lastPlayedIdx = idx;
  pool.lastPlayedAt = Date.now();

  const entry: ActivePlay = {
    howl,
    soundId,
    eventId,
    tier: def.tier,
    baseVolume: volume,
    startedAt: Date.now(),
    duckedTo: null,
    fadeOutTimer: null,
  };
  pool.active.push(entry);
  globalActive.push(entry);

  howl.once(
    'end',
    () => {
      releaseVoice(pool, soundId);
    },
    soundId,
  );

  // Schedule fade-out at the tail of the natural duration.
  if (fadeOut > 0) {
    let durationSec = 0;
    try {
      durationSec = howl.duration(soundId);
    } catch {
      // Howler throws if the sprite isn't ready (common in tests where
      // the audio file doesn't actually load). Skip scheduled fade-out;
      // the sound will hard-end on its own or get smooth-stopped if
      // displaced by the global cap.
      durationSec = 0;
    }
    if (durationSec > 0) {
      const fadeStartMs = Math.max(0, durationSec * 1000 - fadeOut);
      entry.fadeOutTimer = window.setTimeout(() => {
        const cur = (howl.volume(soundId) as number | undefined) ?? volume;
        howl.fade(cur, 0, fadeOut, soundId);
      }, fadeStartMs);
    }
  }

  emit('sound.played', { event_id: eventId, variant_idx: idx });
}

// ---------------------------------------------------------------------------
// Stop helpers
// ---------------------------------------------------------------------------

/**
 * Stop a looping sound with smooth fade-out.
 *
 * JB 2026-05-25 hard rule: «эмбиент всегда должен звучать». Tier 0 (bed)
 * loops cannot be stopped from outside — only replaced via the loop branch
 * in playSound (cross-fade to new variant). Calls with bed event_id are
 * silent no-op. AudioAtmosphere watchdog will respawn the loop if it
 * somehow falls anyway.
 */
export function stopSound(eventId: SoundEventId): void {
  const def = SOUND_MANIFEST[eventId];
  if (def.tier === 0) {
    // Bed is sacred — refuse to silence it.
    return;
  }
  const pool = pools.get(eventId);
  if (pool?.loopingHowl === undefined || pool.loopingHowl === null) return;
  const howl = pool.loopingHowl;
  const cur = howl.volume();
  howl.fade(cur, 0, 800);
  window.setTimeout(() => {
    howl.stop();
    pool.loopingHowl = null;
  }, 850);
}

/**
 * Returns true iff this event has an active looping howl that is currently
 * playing (not paused, not stopped). Used by AudioAtmosphere watchdog to
 * detect ambient drop-out and restart.
 */
export function isLoopPlaying(eventId: SoundEventId): boolean {
  const pool = pools.get(eventId);
  if (pool?.loopingHowl === undefined || pool.loopingHowl === null) return false;
  // Howler `playing()` returns boolean — false for paused/finished.
  return pool.loopingHowl.playing();
}

/**
 * Установить multiplier громкости для текущего loop'а через cross-fade.
 *
 * JB задумка task #92: «каждая эпоха наследует звуки предыдущих с
 * убывающим весом». Реализация: AudioAtmosphere вызывает playSound
 * для всех ambient_epoch{1..N} одновременно, и через эту функцию
 * приглушает старые слои (epoch 1 на 30%, epoch 2 на 60%, epoch 3
 * на 100% — звук становится «оркестром эпох»).
 *
 * `multiplier` — 0..1 относительно базовой громкости def.volume × master.
 *
 * No-op если loop не играет.
 */
export function setLoopVolumeMultiplier(eventId: SoundEventId, multiplier: number): void {
  const pool = pools.get(eventId);
  if (pool?.loopingHowl?.playing() !== true) return;
  const def = SOUND_MANIFEST[eventId];
  const settings = useSoundSettings.getState();
  const baseVolume = settings.effectiveVolume(def);
  const target = Math.max(0, Math.min(1, baseVolume * multiplier));
  const current = pool.loopingHowl.volume();
  if (Math.abs(current - target) < 0.001) return; // уже на нужной громкости
  pool.loopingHowl.fade(current, target, 800);
}

/**
 * Принудительно переключить вариант играющего loop'а с cross-fade.
 *
 * JB 2026-05-26: «почему на фоне все время играет один и тот же эмбиент?».
 * Манифест содержит 4-6 вариантов для каждого ambient_epoch{N}, но
 * `playSound(loop)` идемпотентен — если loop уже играет, variant не
 * ротируется. AudioAtmosphere вызывает эту функцию каждые 90-180 sec
 * чтобы получить мягкую смену звукового пейзажа без перерыва.
 *
 * No-op если:
 *  - eventId неизвестен
 *  - pool ещё не создан (init не вызван)
 *  - loopingHowl null или не играет (нечего ротировать)
 *  - всего 1 variant в пуле (нет куда ротироваться)
 */
export function cycleLoopVariant(eventId: SoundEventId): void {
  const pool = pools.get(eventId);
  if (pool === undefined) return;
  if (pool.loopingHowl?.playing() !== true) return;
  if (pool.howls.length < 2) return; // Один вариант — некуда переключаться

  const def = SOUND_MANIFEST[eventId];
  const settings = useSoundSettings.getState();
  const volume = settings.effectiveVolume(def);
  if (volume === 0) return; // Muted — no point swapping silent variant.

  // Cross-fade длиннее обычного forced-stop'а — петля плавно растворяется
  // и новый трек поднимается из тишины параллельно.
  const FADE_MS = 2500;

  // Fade-out + stop старого howl'а (через setTimeout — Howler.fade асинхронен).
  const oldHowl = pool.loopingHowl;
  const oldVol = oldHowl.volume();
  oldHowl.fade(oldVol, 0, FADE_MS);
  window.setTimeout(() => {
    oldHowl.stop();
  }, FADE_MS + 80);

  // Pick new variant (pickVariantIdx уже исключает lastPlayedIdx → даст
  // другой trek). Sтartим параллельно с fade-in.
  const idx = pickVariantIdx(pool);
  const newHowl = pool.howls[idx];
  if (newHowl === undefined) {
    emit('sound.failed', { event_id: eventId, reason: 'cycle_invalid_idx' });
    return;
  }
  newHowl.volume(0);
  newHowl.play();
  newHowl.fade(0, volume, FADE_MS);

  pool.loopingHowl = newHowl;
  pool.lastPlayedIdx = idx;
  pool.lastPlayedAt = Date.now();
  emit('sound.played', { event_id: eventId, variant_idx: idx });
}

/**
 * Snapshot of currently-playing sounds, grouped by tier. For DebugBadge —
 * JB 2026-05-25: «в дебаг меню отображалось какой звук на каждом слое
 * прямо сейчас играет». Includes both global active (tier ≥1) and bed
 * loops (tier 0). Read-only — call in a polling interval.
 */
export interface ActiveSoundSnapshot {
  readonly eventId: SoundEventId;
  readonly tier: number;
  readonly startedAt: number;
  readonly isLoop: boolean;
}

export function getActiveSounds(): readonly ActiveSoundSnapshot[] {
  const out: ActiveSoundSnapshot[] = [];
  // Tier 0 (bed loops) live in pool.loopingHowl, not globalActive.
  for (const [eventId, pool] of pools) {
    if (pool.loopingHowl?.playing() === true) {
      const def = SOUND_MANIFEST[eventId];
      out.push({
        eventId,
        tier: def.tier,
        startedAt: 0,
        isLoop: true,
      });
    }
  }
  // Tier ≥1 — one-shots tracked in globalActive.
  for (const inst of globalActive) {
    out.push({
      eventId: inst.eventId,
      tier: inst.tier,
      startedAt: inst.startedAt,
      isLoop: false,
    });
  }
  return out;
}

export function stopAllLoops(): void {
  for (const [, pool] of pools) {
    if (pool.loopingHowl?.playing() === true) {
      const howl = pool.loopingHowl;
      const cur = howl.volume();
      howl.fade(cur, 0, 600);
      window.setTimeout(() => {
        howl.stop();
        pool.loopingHowl = null;
      }, 650);
    }
  }
}

/** For tests — wipe state between test cases. */
export function _resetPlayerForTests(): void {
  for (const [, pool] of pools) {
    for (const h of pool.howls) h.unload();
  }
  pools.clear();
  globalActive.length = 0;
  if (releaseTimer !== null) {
    window.clearTimeout(releaseTimer);
    releaseTimer = null;
  }
}
